import React, { useEffect, useState } from 'react';
import { Box, Fade, useScrollTrigger } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { keyframes } from '@mui/system';

interface ScrollIndicatorProps {
    /**
     * Scroll threshold in pixels before the indicator disappears
     */
    threshold?: number;
    /**
     * Custom color for the arrow icon
     */
    iconColor?: string;
    /**
     * Scroll distance when clicked (in viewport height percentage)
     * 1 = one full viewport height
     */
    scrollDistance?: number;
    /**
     * Position from the bottom of the screen in pixels
     */
    bottom?: number;
    /**
     * Position from the right of the screen in pixels
     */
    right?: number;
    /**
     * Size of the indicator button in pixels
     */
    size?: number;
    /**
     * Background color of the indicator button
     */
    backgroundColor?: string;
    /**
     * Background opacity (0-1)
     */
    backgroundOpacity?: number;
    /**
     * When true, the indicator only disappears at the bottom of the page
     * rather than after scrolling past the threshold
     */
    hideOnlyAtBottom?: boolean;
    /**
     * When true, forces the indicator to be visible regardless of scroll position
     */
    forceVisible?: boolean;
    /**
     * Custom z-index for the indicator
     */
    zIndex?: number;
}

/**
 * A reusable scroll indicator that appears in the bottom right corner 
 * and disappears when the user scrolls past a threshold
 */
const ScrollIndicator: React.FC<ScrollIndicatorProps> = ({
    threshold = 100,
    iconColor = '#FC5208', // Orange by default
    scrollDistance = 1,
    bottom = 10,
    right = 10,
    size = 40,
    backgroundColor = 'rgba(255, 255, 255, 0.15)',
    backgroundOpacity = 1,
    hideOnlyAtBottom = false,
    forceVisible = false,
    zIndex = 1000
}) => {
    // Create scroll trigger to detect when user has scrolled
    const scrolled = useScrollTrigger({
        disableHysteresis: true,
        threshold,
    });
    
    // State to check if user has scrolled to bottom of page
    const [isAtBottom, setIsAtBottom] = useState(false);
    
    // Effect to detect if user is at bottom of page
    useEffect(() => {
        const handleScroll = () => {
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            
            // Consider "bottom" when within ~10px of the actual bottom
            const atBottom = windowHeight + scrollTop >= documentHeight - 10;
            setIsAtBottom(atBottom);
        };
        
        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial check
        
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    
    // Determine if the indicator should be visible based on props
    const isVisible = forceVisible ? true : (hideOnlyAtBottom ? !isAtBottom : !scrolled);
    
    // Define bounce animation
    const bounceAnimation = keyframes`
        0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
        }
        40% {
            transform: translateY(-8px);
        }
        60% {
            transform: translateY(-4px);
        }
    `;

    // Handle click to scroll down
    const handleScrollDown = () => {
        window.scrollBy({
            top: window.innerHeight * scrollDistance,
            behavior: 'smooth'
        });
    };

    return (
        <Fade in={isVisible}>
            <Box 
                sx={{
                    position: 'fixed',
                    bottom: `${bottom}px`,
                    right: `${right}px`,
                    zIndex: zIndex,
                    width: `${size}px`,
                    height: `${size}px`,
                    borderRadius: '50%',
                    backgroundColor,
                    opacity: backgroundOpacity,
                    backdropFilter: 'blur(8px)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)',
                    cursor: 'pointer',
                    animation: `${bounceAnimation} 2s infinite`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    }
                }}
                onClick={handleScrollDown}
            >
                <KeyboardArrowDownIcon 
                    sx={{ 
                        color: iconColor, 
                        fontSize: `${Math.round(size * 0.55)}px` 
                    }} 
                />
            </Box>
        </Fade>
    );
};

export default ScrollIndicator; 